import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import autopartsApi, {
  readStoredAuthSession,
  type ApiUploadProgress,
  type AuthenticatedUser,
  type ConversationMediaAttachmentView,
  type ShopConversationView,
} from "@/api/autoparts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FileImage,
  Film,
  ImagePlus,
  Loader2,
  MessageCircle,
  RefreshCw,
  Search,
  Send,
  X,
} from "@/components/icons";
import { cn } from "@/lib/utils";
import { ShopMessageAttachment } from "@/components/chat/shop-message-attachment";
import { toast } from "sonner";

const SUPPORT_VENDOR_ID = "sparelynk-admin-support";
const SUPPORT_NAME = "SpareLynk Support";
const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;

type SupportRequesterRole = "buyer" | "seller";

function supportRequesterRole(conversation: ShopConversationView): SupportRequesterRole {
  const sellerMarker = /\s*·\s*seller\s*$/i;
  if (sellerMarker.test(conversation.buyerName)) return "seller";
  return conversation.messages.some(
    (message) =>
      message.senderRole === "buyer" && sellerMarker.test(message.senderName),
  )
    ? "seller"
    : "buyer";
}

function supportRequesterName(conversation: ShopConversationView): string {
  return conversation.buyerName.replace(/\s*·\s*seller\s*$/i, "").trim() || "User";
}

const ACCEPTED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/webm",
  "audio/mpeg",
  "audio/mp4",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "application/pdf",
];

function currentUser(): AuthenticatedUser | null {
  return readStoredAuthSession()?.user ?? null;
}

function messageTime(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }).format(date);
}

function fileKind(contentType: string) {
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("video/")) return "video";
  if (contentType.startsWith("audio/")) return "audio";
  return "document";
}

function ConversationThread({
  conversation,
  ownRole,
}: {
  conversation: ShopConversationView | null;
  ownRole: "buyer" | "seller";
}) {
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [conversation?.messages.length]);

  if (!conversation || conversation.messages.length === 0) {
    return (
      <div className="flex min-h-[360px] flex-1 flex-col items-center justify-center px-6 py-14 text-center text-muted-foreground">
        <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <MessageCircle className="h-8 w-8" />
        </div>
        <p className="font-semibold text-foreground">Start a support conversation</p>
        <p className="mt-2 max-w-sm text-sm leading-6">
          Send a message or attach a photo, video, audio recording, or PDF about your concern.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-[360px] flex-1 space-y-5 overflow-y-auto bg-muted/20 px-3 py-5 sm:px-6">
      {conversation.messages.map((message, index) => {
        const isOwn = message.senderRole === ownRole;
        const attachments = message.attachments ?? [];
        return (
          <div
            key={message.id || `${message.createdAt}-${index}`}
            className={cn("flex", isOwn ? "justify-end" : "justify-start")}
          >
            <div className="max-w-[92%] sm:max-w-[78%]">
              <div
                className={cn(
                  "rounded-2xl px-3.5 py-3 shadow-sm sm:px-4",
                  isOwn
                    ? "rounded-br-md bg-primary text-primary-foreground"
                    : "rounded-bl-md border bg-card text-card-foreground",
                )}
              >
                <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] opacity-75">
                  <span className="font-semibold">
                    {message.senderName || (isOwn ? "You" : SUPPORT_NAME)}
                  </span>
                  <span>{messageTime(message.createdAt)}</span>
                </div>
                {message.message ? (
                  <p className="whitespace-pre-wrap break-words text-sm leading-6">
                    {message.message}
                  </p>
                ) : null}
                {attachments.length > 0 ? (
                  <div className={cn("grid gap-2", message.message && "mt-3")}>
                    {attachments.map((attachment) => (
                      <ShopMessageAttachment
                        key={attachment.id || attachment.url}
                        attachment={attachment}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}

type MessageComposerProps = {
  draft: string;
  onDraftChange: (value: string) => void;
  files: File[];
  onFilesChange: (files: File[]) => void;
  onSubmit: (event: FormEvent) => void;
  disabled?: boolean;
  sending: boolean;
  uploadProgress: number | null;
  placeholder: string;
};

function MessageComposer({
  draft,
  onDraftChange,
  files,
  onFilesChange,
  onSubmit,
  disabled,
  sending,
  uploadProgress,
  placeholder,
}: MessageComposerProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previewUrls = useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [files],
  );

  useEffect(
    () => () => {
      previewUrls.forEach(({ url }) => URL.revokeObjectURL(url));
    },
    [previewUrls],
  );

  const selectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (incoming.length === 0) return;

    const invalid = incoming.find(
      (file) =>
        !ACCEPTED_ATTACHMENT_TYPES.includes(file.type) ||
        file.size <= 0 ||
        file.size > (file.type.startsWith("video/") ? 100 * 1024 * 1024 : MAX_ATTACHMENT_BYTES),
    );
    if (invalid) {
      toast.error("Unsupported attachment", {
        description: "Use an image, MP4/WEBM video (100 MB), audio file or PDF (12 MB).",
      });
      return;
    }

    const next = [...files, ...incoming].slice(0, MAX_ATTACHMENTS);
    if (files.length + incoming.length > MAX_ATTACHMENTS) {
      toast.info(`Only ${MAX_ATTACHMENTS} attachments can be sent at once.`);
    }
    onFilesChange(next);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  const canSend = Boolean(draft.trim() || files.length > 0) && !disabled && !sending;

  return (
    <form onSubmit={onSubmit} className="border-t bg-card p-3 sm:p-4">
      {files.length > 0 ? (
        <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {previewUrls.map(({ file, url }, index) => {
            const kind = fileKind(file.type);
            return (
              <div key={`${file.name}-${file.lastModified}`} className="relative overflow-hidden rounded-xl border bg-muted/40">
                {kind === "image" ? (
                  <img src={url} alt={file.name} className="h-24 w-full object-cover" />
                ) : (
                  <div className="flex h-24 flex-col items-center justify-center gap-2 px-2 text-center">
                    {kind === "video" ? <Film className="h-7 w-7" /> : <FileImage className="h-7 w-7" />}
                    <span className="line-clamp-2 text-[11px] font-medium">{file.name}</span>
                  </div>
                )}
                <Button variant="ghost" size="icon"
                  type="button"
                  onClick={() => onFilesChange(files.filter((_, fileIndex) => fileIndex !== index))}
                  className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-background/90 shadow-sm hover:bg-background"
                  aria-label={`Remove ${file.name}`}
                  disabled={sending}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      ) : null}

      {uploadProgress !== null ? (
        <div className="mb-3">
          <div className="mb-1 flex justify-between text-xs text-muted-foreground">
            <span>Uploading attachments</span>
            <span>{Math.round(uploadProgress)}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${Math.max(2, Math.min(100, uploadProgress))}%` }}
            />
          </div>
        </div>
      ) : null}

      <div className="flex items-end gap-2 rounded-2xl border bg-background p-2 shadow-sm focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10 sm:gap-3">
        <Input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,audio/*,application/pdf"
          multiple
          className="hidden"
          onChange={selectFiles}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 rounded-xl"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || sending || files.length >= MAX_ATTACHMENTS}
          aria-label="Attach media"
        >
          <ImagePlus className="h-5 w-5" />
        </Button>
        <Textarea
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || sending}
          maxLength={2000}
          rows={1}
          className="max-h-36 min-h-10 flex-1 resize-none border-0 bg-transparent px-1 py-2 shadow-none focus-visible:ring-0"
        />
        <Button
          type="submit"
          size="icon"
          className="shrink-0 rounded-xl"
          disabled={!canSend}
          aria-label="Send message"
        >
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </Button>
      </div>
      <p className="mt-2 px-1 text-[11px] text-muted-foreground">
        Enter to send · Shift+Enter for a new line · Up to 4 files; videos 100 MB, others 12 MB
      </p>
    </form>
  );
}

async function uploadFiles({
  files,
  buyerId,
  senderId,
  senderRole,
  onProgress,
}: {
  files: File[];
  buyerId: string;
  senderId: string;
  senderRole: "buyer" | "seller";
  onProgress: (value: number | null) => void;
}) {
  const uploaded: ConversationMediaAttachmentView[] = [];
  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    const attachment = await autopartsApi.shopConversations.uploadAttachment(
      file,
      {
        buyerId,
        vendorId: SUPPORT_VENDOR_ID,
        senderId,
        senderRole,
      },
      (progress: ApiUploadProgress) => {
        const filePercent = progress.percent ?? 0;
        onProgress(((index + filePercent / 100) / files.length) * 100);
      },
    );
    uploaded.push(attachment);
  }
  onProgress(files.length > 0 ? 100 : null);
  return uploaded;
}

function RequesterSupport({ requesterRole }: { requesterRole: "buyer" | "seller" }) {
  const user = useMemo(currentUser, []);
  const requesterLabel = requesterRole === "seller" ? "Seller" : "Buyer";
  const [conversation, setConversation] = useState<ShopConversationView | null>(null);
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const loadConversation = useCallback(async () => {
    if (!user?.id) return;
    try {
      const next = await autopartsApi.shopConversations.get(user.id, SUPPORT_VENDOR_ID);
      setConversation({
        ...next,
        buyerName: next.buyerName || `${user.name || requesterLabel}${requesterRole === "seller" ? " · Seller" : ""}`,
        sellerName: SUPPORT_NAME,
      });
      await autopartsApi.shopConversations
        .markRead({
          buyerId: user.id,
          vendorId: SUPPORT_VENDOR_ID,
          readerId: user.id,
          readerRole: "buyer",
        })
        .catch(() => undefined);
    } catch (error) {
      toast.error("Unable to load support chat", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [requesterLabel, requesterRole, user]);

  useEffect(() => {
    void loadConversation();
    if (!user?.id) return;
    const unsubscribe = autopartsApi.shopConversations.subscribe({
      participantId: user.id,
      role: "buyer",
      vendorId: SUPPORT_VENDOR_ID,
      onEvent: (event) => {
        if (event.vendorId === SUPPORT_VENDOR_ID) void loadConversation();
      },
    });
    const interval = window.setInterval(() => void loadConversation(), 15000);
    return () => {
      unsubscribe();
      window.clearInterval(interval);
    };
  }, [loadConversation, user?.id]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const message = draft.trim();
    if (!user?.id || (!message && files.length === 0) || sending) return;
    setSending(true);
    setUploadProgress(files.length > 0 ? 0 : null);
    try {
      const attachments = await uploadFiles({
        files,
        buyerId: user.id,
        senderId: user.id,
        senderRole: "buyer",
        onProgress: setUploadProgress,
      });
      await autopartsApi.shopConversations.sendMessage({
        buyerId: user.id,
        vendorId: SUPPORT_VENDOR_ID,
        buyerName: `${user.name || requesterLabel}${requesterRole === "seller" ? " · Seller" : ""}`,
        sellerName: SUPPORT_NAME,
        senderId: user.id,
        senderRole: "buyer",
        senderName: `${user.name || requesterLabel}${requesterRole === "seller" ? " · Seller" : ""}`,
        message,
        attachments,
      });
      setDraft("");
      setFiles([]);
      await loadConversation();
    } catch (error) {
      toast.error("Message not sent", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setSending(false);
      setUploadProgress(null);
    }
  };

  return (
    <main className="mx-auto flex min-h-[calc(100svh-5rem)] w-full max-w-6xl flex-col px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <Badge variant="secondary" className="mb-2">{requesterLabel} support</Badge>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Support center</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Message the SpareLynk admin team and attach supporting media. Do not share passwords,
            one-time codes, or authentication tokens.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">info@sparelynk.com</p>
      </div>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden border-border/70 shadow-lg shadow-black/5">
        <CardHeader className="flex-row items-center justify-between gap-3 border-b bg-card/95 px-4 py-3 sm:px-5 sm:py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <MessageCircle className="h-5 w-5" />
              <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-card bg-success" />
            </div>
            <div className="min-w-0">
              <CardTitle className="truncate text-base sm:text-lg">{SUPPORT_NAME}</CardTitle>
              <CardDescription className="truncate text-xs sm:text-sm">
                {requesterRole === "seller"
                  ? "Account, credential verification, listing, order, and payment assistance"
                  : "Account, order, payment, and marketplace assistance"}
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => void loadConversation()}
            aria-label="Refresh conversation"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          {loading ? (
            <div className="flex min-h-[360px] flex-1 items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
          ) : (
            <ConversationThread conversation={conversation} ownRole="buyer" />
          )}
          <MessageComposer
            draft={draft}
            onDraftChange={setDraft}
            files={files}
            onFilesChange={setFiles}
            onSubmit={send}
            sending={sending}
            uploadProgress={uploadProgress}
            placeholder="Write your message to support..."
          />
        </CardContent>
      </Card>
    </main>
  );
}

export function BuyerSupport() {
  return <RequesterSupport requesterRole="buyer" />;
}

export function SellerSupport() {
  return <RequesterSupport requesterRole="seller" />;
}

export function AdminSupport({ compact = false }: { compact?: boolean } = {}) {
  const admin = useMemo(currentUser, []);
  const [conversations, setConversations] = useState<ShopConversationView[]>([]);
  const [selectedBuyerId, setSelectedBuyerId] = useState("");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const next = await autopartsApi.shopConversations.listForSeller(SUPPORT_VENDOR_ID, {
        forceRefresh: true,
      });
      setConversations(next);
      setSelectedBuyerId((current) =>
        next.some((conversation) => conversation.buyerId === current)
          ? current
          : next[0]?.buyerId || "",
      );
    } catch (error) {
      toast.error("Unable to load support chats", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConversations();
    const unsubscribe = autopartsApi.shopConversations.subscribe({
      participantId: SUPPORT_VENDOR_ID,
      role: "seller",
      onEvent: () => void loadConversations(),
    });
    const interval = window.setInterval(() => void loadConversations(), 15000);
    return () => {
      unsubscribe();
      window.clearInterval(interval);
    };
  }, [loadConversations]);

  const selected =
    conversations.find((item) => item.buyerId === selectedBuyerId) ?? null;
  const filtered = conversations.filter((item) => {
    const needle = search.trim().toLowerCase();
    const requesterRole = supportRequesterRole(item);
    const requesterName = supportRequesterName(item);
    return (
      !needle ||
      requesterName.toLowerCase().includes(needle) ||
      requesterRole.includes(needle) ||
      item.buyerId.toLowerCase().includes(needle)
    );
  });

  useEffect(() => {
    setDraft("");
    setFiles([]);
    if (!selected || !admin?.id) return;
    void autopartsApi.shopConversations
      .markRead({
        buyerId: selected.buyerId,
        vendorId: SUPPORT_VENDOR_ID,
        readerId: SUPPORT_VENDOR_ID,
        readerRole: "seller",
      })
      .catch(() => undefined);
  }, [admin?.id, selected?.buyerId]);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const message = draft.trim();
    if (!selected || (!message && files.length === 0) || sending) return;
    setSending(true);
    setUploadProgress(files.length > 0 ? 0 : null);
    try {
      const attachments = await uploadFiles({
        files,
        buyerId: selected.buyerId,
        senderId: SUPPORT_VENDOR_ID,
        senderRole: "seller",
        onProgress: setUploadProgress,
      });
      await autopartsApi.shopConversations.sendMessage({
        buyerId: selected.buyerId,
        vendorId: SUPPORT_VENDOR_ID,
        buyerName: selected.buyerName || "Buyer",
        sellerName: SUPPORT_NAME,
        senderId: SUPPORT_VENDOR_ID,
        senderRole: "seller",
        senderName: admin?.name ? `${admin.name} · Admin` : SUPPORT_NAME,
        message,
        attachments,
      });
      setDraft("");
      setFiles([]);
      await loadConversations();
    } catch (error) {
      toast.error("Message not sent", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setSending(false);
      setUploadProgress(null);
    }
  };

  return (
    <main className={compact ? "flex h-full min-h-0 w-full flex-col px-3 py-4" : "mx-auto flex min-h-[calc(100svh-5rem)] w-full max-w-[1500px] flex-col px-3 py-4 sm:px-6 sm:py-6 lg:px-8"}>
      <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <Badge variant="secondary" className="mb-2">Admin support inbox</Badge>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Marketplace support</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Review conversations, media, and marketplace user concerns from one workspace.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadConversations()}>
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className={compact ? "flex min-h-0 flex-1 flex-col gap-3" : "grid min-h-0 flex-1 gap-4 lg:grid-cols-[330px_minmax(0,1fr)]"}>
        <Card className="flex min-h-0 flex-col overflow-hidden border-border/70 shadow-sm">
          <CardHeader className="border-b px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Conversations</CardTitle>
              <Badge variant="outline">{filtered.length}</Badge>
            </div>
            <div className="relative pt-2">
              <Search className="absolute left-3 top-5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search buyers or sellers"
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className={compact ? "max-h-40 flex-1 space-y-2 overflow-y-auto p-2" : "max-h-[280px] flex-1 space-y-2 overflow-y-auto p-2 lg:max-h-none"}>
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : null}
            {!loading && filtered.length === 0 ? (
              <p className="px-3 py-10 text-center text-sm text-muted-foreground">
                No support chats yet.
              </p>
            ) : null}
            {filtered.map((conversation) => {
              const requesterRole = supportRequesterRole(conversation);
              const requesterName = supportRequesterName(conversation);
              const last = conversation.messages[conversation.messages.length - 1];
              const lastText =
                last?.message ||
                (last?.attachments?.length
                  ? `${last.attachments.length} attachment${last.attachments.length > 1 ? "s" : ""}`
                  : "No messages");
              return (
                <Button variant="ghost"
                  key={conversation.buyerId}
                  type="button"
                  onClick={() => setSelectedBuyerId(conversation.buyerId)}
                  className={cn(
                    "flex w-full flex-col items-stretch justify-start rounded-xl border p-3 text-left transition-all",
                    selectedBuyerId === conversation.buyerId
                      ? "border-primary/40 bg-primary/8 shadow-sm"
                      : "border-transparent hover:border-border hover:bg-muted/60",
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted font-semibold text-foreground">
                      {requesterName.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <p className="truncate text-sm font-semibold">{requesterName}</p>
                          <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px] capitalize">
                            {requesterRole}
                          </Badge>
                        </div>
                        {conversation.unseenCount > 0 ? (
                          <Badge className="h-5 min-w-5 justify-center px-1.5 text-[10px]">
                            {conversation.unseenCount}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-1 truncate text-xs text-muted-foreground">{lastText}</p>
                    </div>
                  </div>
                </Button>
              );
            })}
          </CardContent>
        </Card>

        <Card className="flex min-h-[560px] min-w-0 flex-col overflow-hidden border-border/70 shadow-lg shadow-black/5 lg:min-h-0">
          <CardHeader className="flex-row items-center justify-between gap-3 border-b px-4 py-3 sm:px-5 sm:py-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 font-bold text-primary">
                {selected ? supportRequesterName(selected).slice(0, 1).toUpperCase() : "U"}
              </div>
              <div className="min-w-0">
                <CardTitle className="truncate text-base sm:text-lg">
                  {selected ? supportRequesterName(selected) : "Select a conversation"}
                </CardTitle>
                <CardDescription className="truncate text-xs sm:text-sm">
                  {selected
                    ? `${supportRequesterRole(selected) === "seller" ? "Seller" : "Buyer"} support conversation`
                    : "Choose a conversation from the inbox"}
                </CardDescription>
              </div>
            </div>
            {selected ? <Badge variant="outline">Active</Badge> : null}
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            <ConversationThread conversation={selected} ownRole="seller" />
            <MessageComposer
              draft={draft}
              onDraftChange={setDraft}
              files={files}
              onFilesChange={setFiles}
              onSubmit={send}
              disabled={!selected}
              sending={sending}
              uploadProgress={uploadProgress}
              placeholder={
                selected
                  ? `Reply to this ${supportRequesterRole(selected)}...`
                  : "Select a conversation first"
              }
            />
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
